/**
 * AI Market Check Widget
 * Fetches market data from Chrono24 API and displays comparison
 */

(function() {
  'use strict';

  const MARKET_API_BASE = 'https://fbej47xkoa.execute-api.us-west-2.amazonaws.com';
  const CHRONO24_HOST = 'https://www.chrono24.com';

  // Handle anchor link clicks to scroll to panel
  function initAnchorLinks() {
    document.addEventListener('click', function(e) {
      const anchorLink = e.target.closest('.AiMarketCheck__AnchorLink');
      if (anchorLink) {
        e.preventDefault();
        const href = anchorLink.getAttribute('href');
        if (href && href.startsWith('#')) {
          const panel = document.querySelector(href);
          if (panel) {
            panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
            anchorLink.setAttribute('aria-expanded', 'true');
            
            // Focus on panel header for keyboard users
            const header = panel.querySelector('.AiMarketCheck__Header');
            if (header) {
              header.focus();
            }
            
            // Trigger data load if needed
            const productId = panel.dataset.productId || panel.id.replace('ai-market-check-panel-', '').replace('ai-market-check-', '');
            if (productId) {
              loadAiMarketCheckData(productId);
            }
          }
        }
      }
    });
  }

  // Load market check data for a specific product
  function loadAiMarketCheckData(productId) {
    const panel = document.getElementById('ai-market-check-panel-' + productId);
    if (!panel) return;
    if (panel.dataset.loaded === 'true') return;
    
    panel.dataset.loaded = 'loading';
    
    // Find the widget container inside the panel
    const widget = panel.querySelector('.AiMarketCheck');
    if (!widget) return;
    
    initMarketCheckWidget(widget);
  }

  // Normalize reference string - clean up whitespace and special characters
  function normalizeRef(ref) {
    if (!ref) return '';
    return ref
      .replace(/\s+/g, ' ')  // Replace multiple spaces/tabs/newlines with single space
      .trim();               // Remove leading/trailing whitespace
  }

  // Fetch Chrono24 market data
  async function fetchChrono24Market({ ref, currency, price, signal }) {
    const normalizedRef = normalizeRef(ref);
    const url = new URL(MARKET_API_BASE + '/market');
    url.searchParams.set('ref', normalizedRef);
    url.searchParams.set('currency', currency || 'USD');
    url.searchParams.set('price', String(price));

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal
    });

    if (!res.ok) {
      throw new Error(`Chrono24 market failed: ${res.status}`);
    }

    const data = await res.json();
    
    // Check if the response contains an error object
    if (data && data.error) {
      throw new Error(data.error.message || 'Chrono24 API error');
    }

    return data;
  }

  // Normalize Chrono24 URL
  function chrono24Url(path) {
    if (!path) return '#';
    if (path.startsWith('http')) return path;
    return CHRONO24_HOST + path;
  }

  // Format currency helper
  function formatCurrency(amount, curr) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  // Format percentage helper
  function formatPercentage(value) {
    const sign = value >= 0 ? '+' : '';
    return sign + value.toFixed(1) + '% vs median';
  }

  // Update summary UI with market data
  function updateSummaryUI(rootEl, data) {
    const ourPriceEl = rootEl.querySelector('.AiMarketCheck__OurPrice');
    const deltaEl = rootEl.querySelector('.AiMarketCheck__Delta');
    const lowValue = rootEl.querySelector('.AiMarketCheck__RangeItem:nth-child(1) .AiMarketCheck__RangeValue');
    const medianValue = rootEl.querySelector('.AiMarketCheck__RangeItem:nth-child(2) .AiMarketCheck__RangeValue');
    const highValue = rootEl.querySelector('.AiMarketCheck__RangeItem:nth-child(3) .AiMarketCheck__RangeValue');
    const confidenceValue = rootEl.querySelector('.AiMarketCheck__ConfidenceValue');
    const matchCountEl = rootEl.querySelector('.AiMarketCheck__MatchCount');

    const currency = rootEl.dataset.currency || 'USD';
    const ourPrice = Number(rootEl.dataset.ourPrice);

    // Update range values
    if (lowValue && typeof data.low === 'number') {
      lowValue.textContent = formatCurrency(data.low, currency);
    }
    if (medianValue && typeof data.median === 'number') {
      medianValue.textContent = formatCurrency(data.median, currency);
    }
    if (highValue && typeof data.high === 'number') {
      highValue.textContent = formatCurrency(data.high, currency);
    }

    // Update delta percentage
    if (deltaEl && typeof data.pctVsMedian === 'number' && !isNaN(data.pctVsMedian)) {
      deltaEl.textContent = formatPercentage(data.pctVsMedian);
    } else if (deltaEl && typeof data.median === 'number' && ourPrice) {
      const pctVsMedian = ((ourPrice - data.median) / data.median) * 100;
      deltaEl.textContent = formatPercentage(pctVsMedian);
    }

    // Update confidence and match count
    if (confidenceValue && data.confidence) {
      confidenceValue.textContent = data.confidence;
    }
    if (matchCountEl && typeof data.matchCount === 'number') {
      matchCountEl.textContent = data.matchCount + ' match' + (data.matchCount !== 1 ? 'es' : '');
    }
  }

  // Parse price from listing item (handles both numeric and formatted string prices)
  function parseListingPrice(item) {
    // If price is already a number, return it
    if (typeof item.price === 'number') {
      return item.price;
    }
    
    // If there's a numeric price field, use that
    if (typeof item.priceNum === 'number') {
      return item.priceNum;
    }
    
    // Try to parse from formatted string (e.g., "$1,234" or "1,234.56")
    if (typeof item.price === 'string' && item.price.trim()) {
      const cleaned = item.price.replace(/[^\d.-]/g, '');
      const parsed = parseFloat(cleaned);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
    
    // Return null if we can't parse
    return null;
  }

  // Calculate LOW, MEDIAN, HIGH from an array of prices
  function calculatePriceStats(prices) {
    if (!prices || prices.length === 0) {
      return { low: null, median: null, high: null };
    }

    // Sort prices ascending
    const sorted = prices.slice().sort(function(a, b) { return a - b; });

    const low = sorted[0];
    const high = sorted[sorted.length - 1];

    // Calculate median
    var median;
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) {
      median = (sorted[mid - 1] + sorted[mid]) / 2;
    } else {
      median = sorted[mid];
    }

    return { low: low, median: median, high: high };
  }

  // Render Chrono24 market listings
  function renderChrono24Market(rootEl, data) {
    const listEl = rootEl.querySelector('.AiMarketCheck__Sources');
    const summaryEl = rootEl.querySelector('.AiMarketCheck__Summary');
    const dividerEl = rootEl.querySelector('.AiMarketCheck__Divider');

    if (!listEl) return;

    listEl.innerHTML = '';

    if (!data || !data.matchCount || !data.listings || !Array.isArray(data.listings) || data.listings.length === 0) {
      // Hide the summary and divider, show only header with message
      if (summaryEl) summaryEl.style.display = 'none';
      if (dividerEl) dividerEl.style.display = 'none';
      listEl.innerHTML = '<div class="AiMarketCheck__NoMatches">No comparable market listings found for this watch.</div>';
      return;
    }

    // Get our price for comparison
    const ourPrice = Number(rootEl.dataset.ourPrice);

    // Filter and sort listings by price proximity to our price
    const listingsWithPrice = data.listings
      .map(function(item) {
        const listingPrice = parseListingPrice(item);
        return {
          item: item,
          price: listingPrice,
          priceDiff: listingPrice !== null ? Math.abs(listingPrice - ourPrice) : Infinity
        };
      })
      .filter(function(entry) {
        // Only include listings with valid prices
        return entry.price !== null && !isNaN(entry.priceDiff);
      })
      .sort(function(a, b) {
        // Sort by price difference (closest first)
        return a.priceDiff - b.priceDiff;
      })
      .slice(0, 5); // Take only the 5 closest

    // Extract prices from top 5 listings for stats calculation
    const top5Prices = listingsWithPrice.map(function(entry) {
      return entry.price;
    });

    // Calculate LOW, MEDIAN, HIGH from top 5 listings only
    const stats = calculatePriceStats(top5Prices);

    // Calculate percentage vs median
    var pctVsMedian = null;
    if (stats.median && ourPrice) {
      pctVsMedian = ((ourPrice - stats.median) / stats.median) * 100;
    }

    // Update summary UI with calculated stats from top 5, plus API confidence/matchCount
    updateSummaryUI(rootEl, {
      low: stats.low,
      median: stats.median,
      high: stats.high,
      pctVsMedian: pctVsMedian,
      confidence: data.confidence,
      matchCount: listingsWithPrice.length
    });

    // Render listings (max 5, closest in price)
    listingsWithPrice.forEach(function(entry) {
      var item = entry.item;
      const row = document.createElement('div');
      row.className = 'AiMarketCheck__SourceItem';
      row.setAttribute('role', 'listitem');

      row.innerHTML =
        '<div class="AiMarketCheck__SourceLogoWrap">' +
          '<span class="AiMarketCheck__SourceName">Chrono24</span>' +
        '</div>' +
        '<div class="AiMarketCheck__ListingMeta">' +
          '<span class="AiMarketCheck__ListingTitle">' +
            (item.brand || '') + (item.brand && item.model ? ' — ' : '') + (item.model || '') +
          '</span>' +
        '</div>' +
        '<div class="AiMarketCheck__ListingPrice">' + (item.price || '') + '</div>';

      listEl.appendChild(row);
    });
  }

  // Render loading state
  function renderLoading(rootEl) {
    const sources = rootEl.querySelector('.AiMarketCheck__Sources');
    if (sources) {
      sources.innerHTML = '<div class="AiMarketCheck__Loading">Loading market data...</div>';
    }
  }

  // Render error state
  function renderError(rootEl, errorDetails) {
    const errorMsg = errorDetails || 'Unable to load market data at this time.';
    const sources = rootEl.querySelector('.AiMarketCheck__Sources');
    if (sources) {
      sources.innerHTML = '<div class="AiMarketCheck__Error">' + errorMsg + '</div>';
    }
  }

  // Initialize market check widget
  function initMarketCheckWidget(rootEl) {
    if (rootEl.dataset.loaded === 'true') return;

    const ref = (rootEl.dataset.ref || '').trim();
    const currency = rootEl.dataset.currency || 'USD';
    const price = Number(rootEl.dataset.ourPrice);

    console.log('[AI Market Check] Initializing widget:', {
      ref: ref,
      currency: currency,
      price: price,
      element: rootEl
    });

    if (!ref || !price || isNaN(price)) {
      console.log('[AI Market Check] Missing required data, hiding widget:', {
        ref: ref,
        price: price,
        hasRef: !!ref,
        hasPrice: !isNaN(price)
      });
      // Hide the widget entirely if price is missing
      rootEl.style.display = 'none';
      // Also hide the parent panel if it exists
      const panel = rootEl.closest('.AiMarketCheck__Panel');
      if (panel) {
        panel.style.display = 'none';
      }
      return;
    }

    rootEl.dataset.loaded = 'true';
    renderLoading(rootEl);

    // Create abort controller for cleanup
    const controller = new AbortController();
    const signal = controller.signal;

    fetchChrono24Market({ ref, currency, price, signal })
      .then(function(data) {
        console.log('[AI Market Check] Received data:', data);
        renderChrono24Market(rootEl, data);
      })
      .catch(function(error) {
        console.error('[AI Market Check] Error:', error);
        renderError(rootEl, 'Unable to load market data.');
      });
  }

  // Initialize all market check widgets
  function initMarketCheck() {
    // Find all AI Market Check widgets (could be in panels or standalone)
    const containers = document.querySelectorAll('.AiMarketCheck, #ai-market-check');
    
    containers.forEach(function(container) {
      // Auto-load all widgets, including those in panels
      // The anchor link click handler will still work for lazy-loading if needed
      initMarketCheckWidget(container);
    });
  }

  // Position AI Market Check panel after shipping/return/contact buttons but before description
  function positionAiMarketCheckPanel() {
    const panels = document.querySelectorAll('[data-ai-market-check-panel="true"]');
    
    panels.forEach(function(panel) {
      const productInfo = panel.closest('.Product__Info');
      if (!productInfo) return;
      
      // Find the description section first
      const description = productInfo.querySelector('.ProductMeta__Description');
      
      // Find elements containing "Shipping Policy", "Returns Policy", or "Contact Us"
      // These buttons are typically in a div after the ProductForm
      const allDivs = productInfo.querySelectorAll('div');
      let buttonsContainer = null;
      
      for (let i = 0; i < allDivs.length; i++) {
        const div = allDivs[i];
        const text = (div.textContent || '').trim();
        
        // Check if this div contains all three button texts
        const hasShipping = /shipping policy/i.test(text);
        const hasReturns = /returns policy/i.test(text);
        const hasContact = /contact us/i.test(text);
        
        if ((hasShipping && hasReturns) || (hasShipping && hasContact) || (hasReturns && hasContact)) {
          // Check if it's after ProductForm and before description
          const productForm = productInfo.querySelector('.ProductForm');
          if (productForm) {
            const isAfterForm = productForm.compareDocumentPosition(div) & Node.DOCUMENT_POSITION_FOLLOWING;
            const isBeforeDesc = !description || 
                               (div.compareDocumentPosition(description) & Node.DOCUMENT_POSITION_PRECEDING);
            
            if (isAfterForm && isBeforeDesc) {
              // Make sure this div doesn't contain the description
              if (!div.querySelector('.ProductMeta__Description') && 
                  !div.closest('.ProductMeta__Description')) {
                if (!buttonsContainer || 
                    productForm.compareDocumentPosition(div) > productForm.compareDocumentPosition(buttonsContainer)) {
                  buttonsContainer = div;
                }
              }
            }
          }
        }
      }
      
      // Position the panel after buttons but before description
      const targetParent = productInfo.querySelector('.Container');
      if (!targetParent) return;
      
      if (buttonsContainer && buttonsContainer.parentElement === targetParent) {
        // Insert panel right after buttons container, but before description
        if (description && description.parentElement === targetParent) {
          // Make sure panel is between buttons and description
          if (buttonsContainer.compareDocumentPosition(panel) !== 0) {
            targetParent.insertBefore(panel, description);
          }
        } else {
          // Insert after buttons if no description found
          if (buttonsContainer.nextSibling !== panel) {
            targetParent.insertBefore(panel, buttonsContainer.nextSibling);
          }
        }
      } else if (description && description.parentElement === targetParent) {
        // Fallback: position right before description
        if (description.previousSibling !== panel) {
          targetParent.insertBefore(panel, description);
        }
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initAnchorLinks();
      initMarketCheck();
      // Position panel after appointment section (with delay to allow app blocks to load)
      setTimeout(positionAiMarketCheckPanel, 500);
      // Also try after a longer delay in case app blocks load slowly
      setTimeout(positionAiMarketCheckPanel, 2000);
    });
  } else {
    initAnchorLinks();
    initMarketCheck();
    setTimeout(positionAiMarketCheckPanel, 500);
    setTimeout(positionAiMarketCheckPanel, 2000);
  }
  
  // Also watch for dynamically added content (MutationObserver)
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      let shouldReposition = false;
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes.length > 0) {
          shouldReposition = true;
        }
      });
      if (shouldReposition) {
        setTimeout(positionAiMarketCheckPanel, 300);
      }
    });
    
    // Observe the product info area for changes
    const productInfo = document.querySelector('.Product__Info');
    if (productInfo) {
      observer.observe(productInfo, {
        childList: true,
        subtree: true
      });
    }
  }
})();
